/**
 * For additional features in this hook, see the following implementations:
 * 1. https://github.com/wellyshen/react-cool-onclickoutside/blob/master/src/index.ts
 * 2. https://github.com/Andarist/use-onclickoutside/blob/main/src/index.ts
 */
import { useEffect } from 'react';

const MOUSEDOWN = 'mousedown';
const TOUCHSTART = 'touchstart';

type HandledEvents = [typeof MOUSEDOWN, typeof TOUCHSTART];
type HandledEventsType = HandledEvents[number];
type PossibleEvent = {
    [Type in HandledEventsType]: HTMLElementEventMap[Type];
}[HandledEventsType];

const currentDocument = typeof document !== 'undefined' ? document : undefined;
const eventTypes = [MOUSEDOWN, TOUCHSTART];

interface Options {
    detectIFrame?: boolean;
}

export const useOutsideClick = (
    // A ref of the element for which we'll check if clicks are outside of
    ref: React.RefObject<HTMLElement>,
    // The function to fire when the user clicks outside our element
    callback: (event: PossibleEvent | FocusEvent) => void,
    { document = currentDocument } = {},
    { detectIFrame = true }: Options = {},
) => {
    const handler = (e: PossibleEvent) => {
        const wasClickOutside = e.target instanceof Node && !ref.current?.contains(e.target);
        if (ref.current && wasClickOutside) {
            callback(e);
        }
    };

    const blurHandler = (e: FocusEvent) =>
        // In Firefox, the iframe becomes document.activeElement in the next event loop
        setTimeout(() => {
            if (document?.activeElement?.tagName === 'IFRAME') {
                callback(e);
            }
        }, 0);

    useEffect(() => {
        if (typeof document === 'undefined') {
            return;
        }

        const removeEventListener = () => {
            eventTypes.forEach(eventType => document.removeEventListener(eventType, handler));

            if (detectIFrame) window.removeEventListener('blur', blurHandler);
        };

        eventTypes.forEach(eventType => {
            document.addEventListener(eventType, handler);
        });

        if (detectIFrame) window.addEventListener('blur', blurHandler);

        return () => removeEventListener();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [detectIFrame]);
};
